import { Box, Button, Heading, Image, Text, useToast } from "@chakra-ui/react";
import { Center, Flex } from "@chakra-ui/layout";
import loginImage from "../assets/login.png";
import Stepper from "../components/Stepper";
import { useRef, useState } from "react";
import Logo from "../components/Logo";
import CreateCenterForm from "../components/forms/CreateCenterForm";
import { NavLink } from "react-router-dom";
import CreateReferentUserForm from "../components/forms/CreateReferentUserForm";
import DigitCodeInput from "../components/inputs/DigitCodeInput";
import PrimaryButton from "../components/PrimaryButton";
import CreateBillingForm, {
	type CreateBillingAddressFormHandle,
} from "../components/forms/CreateBillingAddressForm";
import BuildingIcon from "../components/icons/BuildingIcon";
import PenIcon from "../components/icons/PenIcon";
import CreateAddressForm, {
	type CreateAddressFormHandler,
} from "../components/forms/CreateAddressForm";
import { useMutation } from "react-query";
import {
	emailValidationCheck,
	emailValidationSend,
	signUp,
} from "../mutations/auth.mutations";
import StlLoader from "../components/StlLoader";
import { useSignupStore } from "../stores/signup.store";

export type Center = {
	id?: number;
	company: string;
	enterpriseId?: number;
	name: string;
	phoneNumber: string;
	email: string;
};

type SignInPageCenterProps = {
	onSubmit: (center: Center) => void;
};

function SignInPageCenter({ onSubmit }: SignInPageCenterProps) {
	const handleOnSubmit = (center: Center) => {
		onSubmit(center);
	};

	return (
		<Flex direction="column" alignItems="center">
			<Logo />
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Information du centre
			</Heading>
			<Text
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Entrez les informations du centre que vous souhaitez inscrire
			</Text>
			<CreateCenterForm onSubmit={handleOnSubmit}></CreateCenterForm>
			<Flex mt="44px">
				Déjà un compte ?&nbsp;
				<Button variant="link" color="brand.700">
					<NavLink to="/login">Se connecter</NavLink>
				</Button>
			</Flex>
		</Flex>
	);
}

export type User = {
	id?: number;
	firstName: string;
	lastName: string;
	phoneNumber?: string;
	email: string;
	password: string;
	role: string;
	access: string;
};

type SignInPageReferentPersonProps = {
	onSubmit: (user: User) => void;
};

function SignInPageReferentPerson({ onSubmit }: SignInPageReferentPersonProps) {
	const handleOnSubmit = (user: User) => {
		onSubmit(user);
	};

	return (
		<Flex direction="column" alignItems="center">
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Personne référente
			</Heading>
			<Text
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Nous vous suggérons d'entrer l'adresse email utilisée au travail
			</Text>
			<CreateReferentUserForm
				onSubmit={handleOnSubmit}
			></CreateReferentUserForm>
		</Flex>
	);
}

type SignInPageCodeProps = {
	onBack: () => void;
	onSubmit: (code: string) => void;
	onSendEmailAgain: () => void;
	email?: string;
};

function SignInPageCode({
	onSubmit,
	onBack,
	onSendEmailAgain,
	email,
}: SignInPageCodeProps) {
	return (
		<Flex direction="column" alignItems="center">
			<Logo />
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Vérifiez vos emails
			</Heading>
			<Box
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Nous avons envoyé un code à{" "}
				<Box color="gray.600" fontWeight={800} fontSize="20px">
					{email}
				</Box>
			</Box>
			<DigitCodeInput mt="4em" onComplete={onSubmit}></DigitCodeInput>
			<Box mt={12}>
				<Box w="100%" textAlign="center" mb={3}>
					Vous n'avez rien reçu ?
				</Box>
				<PrimaryButton w="600px" onClick={onSendEmailAgain}>
					Renvoyer l'email
				</PrimaryButton>
			</Box>
			<Flex mt="44px">
				<Button variant="link" color="brand.700" onClick={onBack}>
					Retour
				</Button>
			</Flex>
		</Flex>
	);
}

export type BillingAddress = {
	streetNumber: string;
	streetName: string;
	postalCode: string;
	city: string;
	country: string;
	email: string;
	siret: string;
	centerCode?: string;
};

type SignInBillingAddressProps = {
	onSubmit: (billingAddresses: BillingAddress[]) => void;
};

function SignInBillingAddress({ onSubmit }: SignInBillingAddressProps) {
	const [errors, setErrors] = useState<string[]>([]);
	const [billingAddresses, setBillingAddresses] = useState<BillingAddress[]>(
		[],
	);
	const [billingAddressEditing, setBillingAddressEditing] =
		useState<BillingAddress | null>(null);
	const billingFormRef = useRef<CreateBillingAddressFormHandle>(null);

	return (
		<Flex direction="column" alignItems="center">
			<Logo />
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Inscription
			</Heading>
			<Text
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Entrez vos informations de facturation
			</Text>
			{billingAddresses.map((address, index) => {
				if (address === billingAddressEditing) {
					return (
						<CreateBillingForm
							ref={billingFormRef}
							onSubmit={(address: BillingAddress) => {
								billingAddressEditing.streetNumber = address.streetNumber;
								billingAddressEditing.streetName = address.streetName;
								billingAddressEditing.postalCode = address.postalCode;
								billingAddressEditing.city = address.city;
								billingAddressEditing.country = address.country;
								billingAddressEditing.email = address.email;
								billingAddressEditing.siret = address.siret;
								billingAddressEditing.centerCode = address.centerCode;
								setBillingAddressEditing(null);
							}}
							defaultValue={billingAddressEditing}
						></CreateBillingForm>
					);
				}
				return (
					<Flex
						mt="1rem"
						h="3rem"
						lineHeight="3rem"
						px="24px"
						w="100%"
						bg="gray.100"
						rounded="xl"
						justifyContent="space-between"
						key={address.streetNumber + address.streetName}
					>
						<Flex>
							<BuildingIcon h="3rem" mr="1rem"></BuildingIcon>
							<Text
								fontWeight={600}
								fontSize="14px"
								textOverflow={"ellipsis"}
								w="22rem"
								overflow={"hidden"}
								whiteSpace={"nowrap"}
							>
								{index === 0 && <>Adresse de facturation par défaut:&nbsp;</>}
								{address.streetNumber} {address.streetName}
								,&nbsp;
								{address.postalCode},&nbsp;
								{address.city}
							</Text>
						</Flex>
						<PenIcon
							h="3rem"
							onClick={() => setBillingAddressEditing(address)}
						></PenIcon>
					</Flex>
				);
			})}

			{billingAddressEditing === null && (
				<CreateBillingForm
					ref={billingFormRef}
					onSubmit={(address: BillingAddress) => {
						setBillingAddresses([...billingAddresses, address]);
						setErrors([]);
					}}
				></CreateBillingForm>
			)}

			{errors.map((error) => {
				return (
					<Text textAlign="left" w="100%" color="red.500">
						{error}
					</Text>
				);
			})}
			<PrimaryButton
				mt="44px"
				onClick={async () => {
					if (billingFormRef.current) {
						// 1. List already filled addresses
						const addresses = [...billingAddresses];
						// 2. Handle form already filled (or almost filled)
						if (billingFormRef.current.isDirty()) {
							const isValid = await billingFormRef.current.valid();
							if (isValid) {
								addresses.push(billingFormRef.current.values());
							} else {
								return; // Form is dirty and not correctly filled
							}
						}
						// 3. Can submit, if one is submitted
						if (addresses.length > 0) {
							onSubmit(addresses);
						} else {
							await billingFormRef.current.valid();
						}
					}
				}}
			>
				Continuer
			</PrimaryButton>
		</Flex>
	);
}

export type Address = {
	streetNumber: string;
	streetName: string;
	postalCode: string;
	city: string;
	country: string;
	centerCode?: string;
};

type SignInShippingAddressProps = {
	onSubmit: (shippingAddresses: Address[]) => void;
};

function SignInShippingAddress({ onSubmit }: SignInShippingAddressProps) {
	const [errors, setErrors] = useState<string[]>([]);
	const [shippingAddresses, setShippingAddresses] = useState<Address[]>([]);
	const [shippingAddressEditing, setShippingAddressEditing] =
		useState<Address | null>(null);
	const shippingFormRef = useRef<CreateAddressFormHandler>(null);

	return (
		<Flex direction="column" alignItems="center">
			<Logo />
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Inscription
			</Heading>
			<Text
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Entrez les informations de livraison
			</Text>

			{shippingAddresses.map((address, index) => {
				if (address === shippingAddressEditing) {
					return (
						<CreateAddressForm
							ref={shippingFormRef}
							onSubmit={(address: Address) => {
								shippingAddressEditing.streetNumber = address.streetNumber;
								shippingAddressEditing.streetName = address.streetName;
								shippingAddressEditing.postalCode = address.postalCode;
								shippingAddressEditing.city = address.city;
								shippingAddressEditing.country = address.country;
								shippingAddressEditing.centerCode = address.centerCode;
								setShippingAddressEditing(null);
							}}
							defaultValue={shippingAddressEditing}
						></CreateAddressForm>
					);
				}
				return (
					<Flex
						mt="1rem"
						h="3rem"
						lineHeight="3rem"
						px="24px"
						w="100%"
						bg="gray.100"
						rounded="xl"
						justifyContent="space-between"
						key={address.streetNumber + address.streetName}
					>
						<Flex>
							<BuildingIcon h="3rem" mr="1rem"></BuildingIcon>
							<Text
								fontWeight={600}
								fontSize="14px"
								textOverflow={"ellipsis"}
								w="22rem"
								overflow={"hidden"}
								whiteSpace={"nowrap"}
							>
								{index === 0 && <>Adresse de livraison par défaut:&nbsp;</>}
								{address.streetNumber} {address.streetName}
								,&nbsp;
								{address.postalCode},&nbsp;
								{address.city}
							</Text>
						</Flex>
						<PenIcon
							h="3rem"
							onClick={() => setShippingAddressEditing(address)}
						></PenIcon>
					</Flex>
				);
			})}

			{shippingAddressEditing === null && (
				<CreateAddressForm
					ref={shippingFormRef}
					onSubmit={(address: Address) => {
						setShippingAddresses([...shippingAddresses, address]);
						setErrors([]);
					}}
				></CreateAddressForm>
			)}

			{errors.map((error) => {
				return (
					<Text textAlign="left" w="100%" color="red.500">
						{error}
					</Text>
				);
			})}
			<PrimaryButton
				mt="44px"
				onClick={async () => {
					if (shippingFormRef.current) {
						// 1. List already filled addresses
						const addresses = [...shippingAddresses];
						// 2. Handle form already filled (or almost filled)
						if (shippingFormRef.current.isDirty()) {
							const isValid = await shippingFormRef.current.valid();
							if (isValid) {
								addresses.push(shippingFormRef.current.values());
							} else {
								return; // Form is dirty and not correctly filled
							}
						}
						// 3. Can submit, if one is submitted
						if (addresses.length > 0) {
							onSubmit(addresses);
						} else {
							await shippingFormRef.current.valid();
						}
					}
				}}
			>
				Valider l'inscription
			</PrimaryButton>
		</Flex>
	);
}

function SignInDone() {
	return (
		<Flex direction="column" alignItems="center">
			<Heading
				fontWeight={600}
				fontSize="32px"
				lineHeight="48px"
				color="gray.900"
				whiteSpace={"nowrap"}
			>
				Inscription en attente de validation
			</Heading>
			<Text
				fontWeight={400}
				fontSize="20px"
				lineHeight="30px"
				color="gray.600"
				textAlign="center"
				display={{ base: "none", sm: "block" }}
			>
				Vous recevrez un mail de confirmation de l'ouverture de votre compte
			</Text>
			<PrimaryButton mt="44px">
				<NavLink to="/login">Continuer</NavLink>
			</PrimaryButton>
		</Flex>
	);
}

function SignInPage() {
	const toast = useToast();
	const [step, setStep] = useState(1);
	const {
		user,
		code,
		center,
		billingAddress,
		shippingAddress,
		setUser,
		setCode,
		setCenter,
		setBillingAddress,
		setShippingAddress,
	} = useSignupStore();
	const sendEmailValidation = useMutation(emailValidationSend, {
		onSuccess: () => {
			setStep(2);
		},
		onError: () => {
			toast({
				title: "Serveur",
				description: "Le code n'a pas pu être envoyé",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		},
	});
	const checkEmailValidation = useMutation(emailValidationCheck, {
		onSuccess: () => {
			setStep(3);
		},
		onError: () => {
			toast({
				title: "Validation",
				description: "Le code ne semble pas valide",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		},
	});
	const sendSignUp = useMutation(signUp, {
		onSuccess: () => {
			setStep(6);
		},
		onError: () => {
			toast({
				title: "Serveur",
				description: "L'inscription n'a pas pu être effectuée",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		},
	});

	return (
		<Flex>
			<Box display={{ base: "none", lg: "block" }}>
				<StlLoader
					alternative={
						<Image
							src={loginImage}
							alt="Dan Abramov"
							h="100vh"
							w="50vw"
							display={{ base: "none", lg: "block" }}
						/>
					}
				></StlLoader>
			</Box>
			<Center w="100%" overflowY={"auto"}>
				{step <= 5 && (
					<Flex
						direction="column"
						h="100vh"
						w="100%"
						px={{ base: "44px", md: "144px" }}
						pt={{ base: "22px" }}
						pb="44px"
					>
						<Stepper step={step} of={5} mb="22px"></Stepper>
						{step === 1 && (
							<SignInPageReferentPerson
								onSubmit={(user: User) => {
									if (sendEmailValidation.isLoading) return;

									setUser(user);
									sendEmailValidation.mutate({
										email: user.email,
									});
								}}
							></SignInPageReferentPerson>
						)}
						{step === 2 && (
							<SignInPageCode
								email={user?.email}
								onSendEmailAgain={() => {
									if (sendEmailValidation.isLoading) return;
									if (user?.email) {
										sendEmailValidation.mutate({
											email: user?.email,
										});
									}
								}}
								onBack={() => setStep(1)}
								onSubmit={(code: string) => {
									if (checkEmailValidation.isLoading) return;

									setCode(code);
									if (user?.email) {
										checkEmailValidation.mutate({
											email: user?.email,
											code: code,
										});
									}
								}}
							></SignInPageCode>
						)}
						{step === 3 && (
							<SignInPageCenter
								onSubmit={(center: Center) => {
									setCenter(center);
									setStep(4);
								}}
							></SignInPageCenter>
						)}
						{step === 4 && (
							<SignInBillingAddress
								onSubmit={(billingAddresses: BillingAddress[]) => {
									if (billingAddresses.length === 0)
										throw new Error("No billing address provided");

									setBillingAddress(billingAddresses[0]);
									setStep(5);
								}}
							></SignInBillingAddress>
						)}
						{step === 5 && (
							<SignInShippingAddress
								onSubmit={(shippingAddresses: Address[]) => {
									if (sendSignUp.isLoading) return;
									if (shippingAddresses.length === 0)
										throw new Error("No shipping address provided");

									setShippingAddress(shippingAddresses[0]);
									sendSignUp.mutate({
										user,
										code,
										center,
										billingAddress,
										shippingAddress,
									});
								}}
							></SignInShippingAddress>
						)}
					</Flex>
				)}
				{step === 6 && (
					<Flex
						direction="column"
						alignItems="center"
						justifyContent="center"
						h="100vh"
						w="100%"
						px={{ base: "44px", md: "144px" }}
						pt="44px"
					>
						<SignInDone></SignInDone>
					</Flex>
				)}
			</Center>
		</Flex>
	);
}

export default SignInPage;
